import React, { useLayoutEffect } from 'react'
import { AiOutlineEdit } from 'react-icons/ai'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { PatientProfile } from '../../api/services/orders/types.ts'
import BodyMeasurements from '../../components/bodyMeasurements/BodyMeasurements.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import HoverHint from '../../components/common/HoverHint'
import PatientSurveys from '../../components/patientSurveys/PatientSurveys.tsx'
import PrepaidVouchers from '../../components/prepaidVouchers/PrepaidVouchers.tsx'
import usePatientProfile from '../../hooks/usePatientProfile.ts'
import { linkToProfilePD3 } from '../../utils/patientProfiles.tsx'
import Orders from '../orders/Orders.tsx'

const PatientProfileDetails = () => {
  const { patientProfileId } = useParams<{ patientProfileId: string }>()
  const { profile, getPatientProfile, error } = usePatientProfile()

  const navigate = useNavigate()

  const location = useLocation()
  const patientProfileFromOrder: PatientProfile =
    location.state?.patientProfileFromOrder

  if (patientProfileId === undefined) return <div>No patient profile id</div>

  useLayoutEffect(() => {
    if (patientProfileId) getPatientProfile(Number(patientProfileId))
  }, [])

  const onClickLinkToProfilePD3 = (pd3_uuid: string) => {
    const url = linkToProfilePD3(pd3_uuid)
    window.open(url, '_blank')
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        pageName="Dane pacjenta"
        breadcrumbs={[
          { label: 'Pacjenci', path: '/patient-profile' },
          { label: 'Dane pacjenta' }
        ]}
      />
      {error && patientProfileFromOrder && (
        <Card className="w-full mb-4">
          <Card.Body>
            <p className="text-red-500">
              Dane pacjenta{' '}
              <span className={'text-xl text-blue-500 font-bold'}>
                {patientProfileFromOrder.first_name}{' '}
                {patientProfileFromOrder.last_name}
              </span>{' '}
              sa niedostępne.
            </p>

            <p className="text-red-500">
              Jest przypisany do kodu przypisania{' '}
              <span className={'text-blue-500 font-bold'}>
                {patientProfileFromOrder.user_assignment_code}
              </span>{' '}
              należącego do innego opiekuna.
            </p>
          </Card.Body>
        </Card>
      )}

      {error && patientProfileFromOrder === undefined && (
        <Card className="w-full mb-4">
          <Card.Body>
            <p className="text-red-500">Brak danych pacjenta w bazie.</p>
          </Card.Body>
        </Card>
      )}

      {profile && (
        <React.Fragment>
          <Card className="w-full mb-4">
            <Card.Title
              className={'px-0 !py-0 md:px-7 md:!py-4'}
              isChildPlainText={false}
            >
              <div
                className={
                  'flex flex-col xl:flex-row items-center gap-2 md:py-0 py-6'
                }
              >
                <div
                  className={
                    'flex w-full md:w-auto flex-col bg-gray-200 dark:bg-gray-700 md:items-center px-4 py-6 md:p-6 md:px-8'
                  }
                >
                  <p
                    className={
                      'text-[40px] w-full leading-10 md:leading-[28px]'
                    }
                  >
                    {profile?.first_name} {profile?.last_name}
                  </p>
                </div>

                <div className="flex flex-1 w-full flex-row flex-wrap md:flex-nowrap gap-x-8 gap-y-4 md:gap-x-16 text-sm md:flex-row md:items-center justify-between md:justify-normal md:border-2 md:border-gray-100 dark:border-gray-700 p-4">
                  <div className="flex flex-col">
                    <p className="text-gray-500 text-[13px]">{'Płeć'}</p>
                    <p className="text-black-2 dark:text-white">
                      {profile.sex === 1 ? 'Mężczyzna' : 'Kobieta'}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-gray-500 text-[13px]">{'Grupa diet'}</p>
                    <p className="text-black-2 dark:text-white">
                      {profile.patient_type}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-gray-500 text-[13px]">
                      {'Kod przypisania'}
                    </p>
                    <p className="text-blue-800 dark:text-white">
                      {profile.user_assignment_code}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-gray-500 text-[13px]">{'Adres e-mail'}</p>
                    <p className="text-black-2 dark:text-white">
                      {profile.preassigned_email}
                    </p>
                  </div>
                </div>

                <Button
                  variant="filled"
                  size="medium"
                  color={'primary'}
                  type="button"
                  icon={<AiOutlineEdit />}
                  onClick={() =>
                    navigate(`/patient-profile/${patientProfileId}/edit`, {
                      state: {
                        patientProfile: profile
                      }
                    })
                  }
                >
                  {'Edytuj dane pacjenta'}
                </Button>
              </div>
            </Card.Title>

            <Card.Body className={'px-4 !py-4 md:px-7 md:!py-4'}>
              <div className="flex flex-row gap-2">
                <p>Pacjent PD3:</p>
                {profile?.pd3_uuid ? (
                  <HoverHint hintLabel={'Przejdź do profilu pacjenta w PD3'}>
                    <div
                      className={'flex flex-row gap-2'}
                      onClick={() => onClickLinkToProfilePD3(profile?.pd3_uuid)}
                    >
                      <p className={'font-bold text-blue-800'}>TAK</p>
                      {profile?.pd3_uuid}
                    </div>
                  </HoverHint>
                ) : (
                  <p className={'font-bold text-red-400'}>NIE</p>
                )}
              </div>
            </Card.Body>
          </Card>

          <div className={'flex flex-col md:flex-row gap-4'}>
            <Card className="md:w-1/2 mb-4">
              <Orders
                wrapInCard={false}
                breadcrumbs={false}
                patientProfileId={profile.id}
                showColumns={[
                  { key: 'id', label: 'Nr' },
                  { key: 'created_at', label: 'Data utw.' },
                  { key: 'status', label: 'Status' },
                  { key: 'subscription_length', label: 'Długość' },
                  { key: 'subscription', label: '' }
                ]}
              />
            </Card>

            <Card className="md:w-1/2 mb-4">
              <PrepaidVouchers patient_profile={profile.id} />
            </Card>
          </div>

          <div className={'flex flex-col md:flex-row gap-4'}>
            <Card className="md:w-1/2 mb-4">
              <BodyMeasurements
                patient_profile={profile.id}
                patientPD3={profile.pd3_uuid}
                patientFullName={`${profile.first_name} ${profile.last_name}`}
              />
            </Card>
            <Card className="md:w-1/2 mb-4">
              <PatientSurveys
                patientProfileId={profile.id}
                patientFullName={`${profile.first_name} ${profile.last_name}`}
                surveys={profile.patient_surveys}
              />
            </Card>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default PatientProfileDetails
